import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Space,
  List,
  Tag,
  Empty,
  Spin,
  message,
} from 'antd';
import { ArrowLeftOutlined, CarOutlined, DownloadOutlined, FileDoneOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { fetchMyReceipts, downloadMyReceiptPdf, fetchPortalPromotion, CustomerReceipt } from '../services/api/receiptsApi';
import { BANNERS } from '../components/Promotions/banners';
import type { CustomerPromotion, BannerDesign } from '../components/Promotions/banners/BannerProps';
import { formatCurrency } from '../utils/formatCurrency';

const { Title, Text } = Typography;

/** "work_order" -> "Work Order #123"; "estimate" -> "Estimate #6528". */
const entityLabel = (entityType: string, entityId: number): string => {
  const words = entityType
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return `${words.join(' ') || 'Job'} #${entityId}`;
};

const kindColor = (kind: CustomerReceipt['kind']): string => (kind === 'refund' ? 'orange' : 'green');

/**
 * The signed-in customer's receipts (WP: receipts-and-promotions).
 *
 * Backed by the `receipts` table — one row per payment or refund, including a
 * deposit against an estimate that never produced an invoice. This replaced
 * an earlier version of this page that listed invoices and rendered its own
 * PDF client-side with `InvoicePDFService`; that path could never show a
 * deposit (no invoice exists yet to render) and is superseded here.
 *
 * Download fetches the PDF as a blob through `customerPortalApi` (see
 * `downloadMyReceiptPdf` in `services/api/receiptsApi.ts`) so the customer's
 * portal auth applies — never a bare link to a storage path, which is not
 * publicly served. A receipt can legitimately have no PDF (its render or
 * storage step failed at issue time; staff resend is the recovery path), and
 * the server 404s identically for that case and for someone else's receipt
 * id. Rather than pre-guess which receipts have a PDF, the control always
 * shows and a 404 on click is handled quietly — `message.info`, not an
 * alarming error — since there's nothing the customer can do about it here.
 */
const CustomerReceipts: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useCustomerAuth();

  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState<CustomerReceipt[]>([]);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleDownload = async (receipt: CustomerReceipt) => {
    setDownloadingId(receipt.id);
    try {
      await downloadMyReceiptPdf(receipt.id, receipt.receipt_number);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        message.info('No PDF is available for this receipt yet.');
      } else {
        message.error('Could not download that receipt.');
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const [promotion, setPromotion] = useState<CustomerPromotion | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        setReceipts(await fetchMyReceipts());
      } catch {
        message.error('Could not load your receipts.');
      } finally {
        setLoading(false);
      }
      // Separately, and deliberately after: the promotion is decoration. If the
      // shop has none, or the call fails, the receipts still render — a marketing
      // banner must never be the reason someone cannot see their own receipts.
      try {
        setPromotion(await fetchPortalPromotion());
      } catch {
        setPromotion(null);
      }
    };
    load();
  }, [isAuthenticated, navigate]);

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
          padding: '40px 20px',
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size={0}>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/customer/dashboard')}
                style={{ padding: 0, marginBottom: 8 }}
              >
                Back to Dashboard
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                <FileDoneOutlined /> My Receipts
              </Title>
              <Text type="secondary">Every payment and refund on your account.</Text>
            </Space>
          </Card>

          {promotion && BANNERS[promotion.bannerDesign as BannerDesign] && (
            <div style={{ marginBottom: 16 }}>
              {React.createElement(BANNERS[promotion.bannerDesign as BannerDesign], { promotion })}
            </div>
          )}

          <Card>
            {receipts.length > 0 ? (
              <List
                dataSource={receipts}
                renderItem={(receipt) => (
                  <List.Item
                    actions={[
                      // aria-label is set unconditionally so an icon-only
                      // button (as it collapses to on a phone) still names
                      // "download my receipt" to a screen reader.
                      <Button
                        key="dl"
                        type="link"
                        size="small"
                        icon={<DownloadOutlined />}
                        aria-label={`Download receipt ${receipt.receipt_number}`}
                        loading={downloadingId === receipt.id}
                        onClick={() => handleDownload(receipt)}
                      >
                        Download
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileDoneOutlined style={{ fontSize: 24, color: '#52c41a' }} />}
                      title={
                        <Space wrap>
                          <Text strong>{receipt.receipt_number}</Text>
                          <Tag color={kindColor(receipt.kind)}>{receipt.kind.toUpperCase()}</Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary">
                            <CarOutlined /> {entityLabel(receipt.entity_type, receipt.entity_id)}
                          </Text>
                          <Text type="secondary">
                            {dayjs(receipt.created_at).format('MMM D, YYYY')} —{' '}
                            {receipt.payment_method.toUpperCase()}
                          </Text>
                        </Space>
                      }
                    />
                    <Text strong style={{ fontSize: 16 }}>
                      {formatCurrency(receipt.amount_cents / 100)}
                    </Text>
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description="No receipts yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: '60px 20px' }}
              >
                <Button type="primary" onClick={() => navigate('/book')}>
                  Book a Service
                </Button>
              </Empty>
            )}
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerReceipts;
